import moment from 'moment';
import {
  DATE_FORMATS,
  RECORD_COLORS,
  INSTRUCTION_LENGTH,
  RECORD_BINDER_STATUS,
  userRoles,
  auditorRoles,
} from '../constants/AppConstants';
import { ATTACHMENT_TYPES } from '../constants/FormConstants';

class RecordBinderHelper {
  static formatExpiryDate(expiryDate) {
    if (!expiryDate) return null;
    return moment(expiryDate).format(DATE_FORMATS.MM_DD_YY);
  }

  static isEndDateBeforeStartDate(startDate, endDate) {
    if (!startDate || !endDate) return false;
    const start = new Date(startDate);
    const end = new Date(endDate);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return end < start;
  }

  static getExpiryDateColor(expiryDateColor) {
    return (expiryDateColor || '').toLowerCase() === RECORD_COLORS.RED
      ? RECORD_COLORS.RED
      : RECORD_COLORS.GREY;
  }

  static getHistoryExpiryStatus(expiryDate, t) {
    if (!expiryDate) return { date: '', color: '', status: null };
    const now = moment();
    const exp = moment(expiryDate);
    const formatted = exp.format(DATE_FORMATS.MM_DD_YYYY);
    const expiresLabel = t.RECORD_HISTORY_EXPIRES || 'Expires:';
    const expiredLabel = t.RECORD_HISTORY_EXPIRED || 'Expired:';
    if (exp.isBefore(now, 'day')) {
      return {
        date: `${expiredLabel} ${formatted}`,
        color: RECORD_COLORS.DEEP_RED,
        status: RECORD_BINDER_STATUS.EXPIRED,
      };
    } else {
      const daysDiff = exp.diff(now, 'days');
      if (daysDiff <= 30) {
        return {
          date: `${expiresLabel} ${formatted}`,
          color: RECORD_COLORS.RED,
        };
      } else {
        return {
          date: `${expiresLabel} ${formatted}`,
          color: RECORD_COLORS.GREY,
        };
      }
    }
  }

  static sortRecordHistoryByDate(recordHistory) {
    if (!recordHistory || !Array.isArray(recordHistory)) {
      return [];
    }

    const currentDate = moment();
    return [...recordHistory].sort((firstRecord, secondRecord) => {
      const firstExpiryDate = firstRecord.expiresOn || firstRecord.expiry_date;
      const secondExpiryDate =
        secondRecord.expiresOn || secondRecord.expiry_date;

      const isFirstRecordExpired =
        firstRecord.status === RECORD_BINDER_STATUS.EXPIRED ||
        (firstExpiryDate &&
          moment(firstExpiryDate).isBefore(currentDate, 'day'));
      const isSecondRecordExpired =
        secondRecord.status === RECORD_BINDER_STATUS.EXPIRED ||
        (secondExpiryDate &&
          moment(secondExpiryDate).isBefore(currentDate, 'day'));

      if (isFirstRecordExpired && !isSecondRecordExpired) return 1;
      if (!isFirstRecordExpired && isSecondRecordExpired) return -1;

      if (!firstExpiryDate && !secondExpiryDate) return 0;
      if (!firstExpiryDate) return 1;
      if (!secondExpiryDate) return -1;

      const firstRecordMoment = moment(firstExpiryDate);
      const secondRecordMoment = moment(secondExpiryDate);

      return secondRecordMoment.valueOf() - firstRecordMoment.valueOf();
    });
  }

  static truncateInstructions(text, maxLength = INSTRUCTION_LENGTH) {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  static getVisibleToDropdownOptions(options) {
    if (Array.isArray(options)) {
      return options.map((item) => ({
        label: item.value || item.name || item.label || String(item.key),
        value: item.key || item.id,
      }));
    }

    if (options.menu_items && Array.isArray(options.menu_items)) {
      return options.menu_items.map((item) => ({
        label: item.value || item.name || item.label || String(item.key),
        value: item.key || item.id,
      }));
    }
    return [];
  }

  static processAttachment(attachmentValue, fileType, recordName) {
    if (
      !attachmentValue ||
      typeof attachmentValue !== 'string' ||
      attachmentValue.trim() === ''
    ) {
      return null;
    }

    let processedAttachmentValue = attachmentValue;
    if (attachmentValue.startsWith('data:')) {
      const base64Index = attachmentValue.indexOf(',');
      processedAttachmentValue =
        base64Index !== -1
          ? attachmentValue.substring(base64Index + 1)
          : attachmentValue;
    }

    if (!processedAttachmentValue || processedAttachmentValue.trim() === '') {
      return null;
    }

    let fileName = recordName || 'Record';
    let attachmentType = 'image';
    let normalizedFileType = '';

    if (fileType) {
      normalizedFileType = fileType.toLowerCase().trim().replace(/^\./, '');
      fileName = fileName + '.' + normalizedFileType;
      attachmentType =
        normalizedFileType === ATTACHMENT_TYPES.PDF
          ? ATTACHMENT_TYPES.PDF
          : 'image';
    }

    return {
      name: fileName,
      base64: processedAttachmentValue,
      type: attachmentType,
      // Coarse `type` above only distinguishes pdf vs image; callers that
      // need to tell PNG from JPEG (e.g. for print/preview mime handling)
      // should use this instead.
      fileType: normalizedFileType,
    };
  }

  // Confirmed shape (matches the mobile app's RecordHistoryAttachment model):
  // recordHistoryDetails.attachments is an array of
  // { attachmentId, attachmentUrl, fileName, fileType }. There is no inline
  // file content - attachmentUrl/attachmentId are references only; fetching
  // the actual bytes for preview/print is a separate step (see
  // AddRecord.handleFilePreview, which fetches via GET_ATTACHMENT).
  static normalizeAttachmentsFromDetails(recordHistoryDetails, legacyFileType) {
    if (!recordHistoryDetails) {
      return [];
    }

    const attachments = recordHistoryDetails.attachments;

    if (Array.isArray(attachments) && attachments.length > 0) {
      return attachments.map((item) => ({
        attachmentId: item.attachmentId || null,
        name: item.displayName || item.fileName || null,
        attachmentUrl: item.attachmentUrl || null,
        base64: null,
        fileType: item.fileType || null,
      }));
    }

    // Legacy shape: a single inline base64 scalar + a single fileType, from
    // a record saved before this migration (no attachments array at all).
    if (recordHistoryDetails.attachment) {
      return [
        {
          attachmentId: null,
          name: null,
          attachmentUrl: null,
          base64: recordHistoryDetails.attachment,
          fileType: recordHistoryDetails.fileType || legacyFileType || null,
        },
      ];
    }

    return [];
  }

  static processAttachments(recordHistoryDetails, recordName, legacyFileType) {
    const normalized = this.normalizeAttachmentsFromDetails(
      recordHistoryDetails,
      legacyFileType,
    );

    return normalized
      .map((item, index) => {
        // Legacy inline content only - current API responses never carry
        // base64 here, only a reference (attachmentId/attachmentUrl).
        if (item.base64) {
          const fallbackName =
            normalized.length > 1
              ? `${recordName || 'Record'}_${index + 1}`
              : recordName;
          const processed = this.processAttachment(
            item.base64,
            item.fileType,
            fallbackName,
          );
          if (processed) {
            return {
              ...processed,
              attachmentId: item.attachmentId,
              attachmentUrl: null,
            };
          }
        }

        if (!item.attachmentId && !item.attachmentUrl) {
          return null;
        }
        return {
          name: item.name || 'Attachment',
          base64: null,
          attachmentUrl: item.attachmentUrl || null,
          type:
            item.fileType === ATTACHMENT_TYPES.PDF
              ? ATTACHMENT_TYPES.PDF
              : 'image',
          attachmentId: item.attachmentId,
          fileType: item.fileType || '',
        };
      })
      .filter(Boolean);
  }

  static getFileExtension(fileName) {
    if (!fileName || typeof fileName !== 'string') {
      return '';
    }
    const lastDotIndex = fileName.lastIndexOf('.');
    return lastDotIndex !== -1
      ? fileName.substring(lastDotIndex + 1).toLowerCase()
      : '';
  }

  static getInitialValues(currentRecord, recordHistoryDetails, recordBinder) {
    if (currentRecord && currentRecord.id) {
      let attachments = [];
      if (recordHistoryDetails) {
        const recordName = recordBinder?.record_name;
        attachments = this.processAttachments(
          recordHistoryDetails,
          recordName,
          currentRecord?.fileType,
        );
      }

      return {
        attachments,
        expiresOn: currentRecord.expiresOn
          ? new Date(currentRecord.expiresOn)
          : null,
      };
    }

    return {
      attachments: [],
      expiresOn: null,
    };
  }

  static canUserViewRecordBinder(user) {
    if (!user || (!user.userRoleId && !user.user_role_key)) {
      return false;
    }

    const userRole = Number(user.userRoleId || user.user_role_key);

    return (
      userRole === userRoles.PROGRAM_MANAGER ||
      userRole === userRoles.TRAINER ||
      userRole === userRoles.FEEDLOT_MANAGER ||
      userRole === userRoles.INTERNAL_AUDITOR ||
      userRole === userRoles.EXTERNAL_AUDITOR
    );
  }

  static canUserWriteRecordBinder(user) {
    if (!user || (!user.userRoleId && !user.user_role_key)) {
      return false;
    }

    const userRole = Number(user.userRoleId || user.user_role_key);

    if (auditorRoles.includes(userRole)) {
      return false;
    }

    return (
      userRole === userRoles.PROGRAM_MANAGER ||
      userRole === userRoles.TRAINER ||
      userRole === userRoles.FEEDLOT_MANAGER
    );
  }

  static printPDF(base64Data, printJS, onError) {
    try {
      printJS({
        printable: base64Data,
        type: 'pdf',
        base64: true,
      });
    } catch (e) {
      console.log('Print error', e);
      if (onError) onError(e);
    }
  }

  static printImage(base64Data, imageType, printJS, onError) {
    try {
      printJS({
        printable: `data:${imageType};base64,${base64Data}`,
        type: 'image',
        imageStyle:
          'display: block; max-width: 100%; max-height: 100vh; margin: auto; object-fit: contain;',
        style:
          '@page { margin: 0; } html, body { margin: 0; padding: 0; height: 100%; overflow: hidden; }',
      });
    } catch (e) {
      console.log('Print error', e);
      if (onError) onError(e);
    }
  }
}

export default RecordBinderHelper;
